import { useTranslation } from "react-i18next";
import type { TypeLineage } from "../../../api/content/typeLineage";
import type { DataTypeRef } from "../../../api/content/workflowsApi";
import { typesHint } from "../../workflows/inputCards/typeWords";
import { editViewsOf } from "../../workflows/presentation/resolvePresentation";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";

interface ValueEditorProps {
  label: string;
  /** The input's data types: an `xsd:boolean` is a checkbox, an `xsd:integer` a whole-number field, an `xsd:decimal` a number field, anything else text. */
  accepts: readonly DataTypeRef[];
  /** Each accepted type's lineage from module B: a project's subtype of `xsd:integer` takes the whole-number field. */
  lineages?: readonly TypeLineage[];
  value: unknown;
  disabled?: boolean;
  onChange: (value: string | number | boolean) => void;
}

/**
 * One value for a step input. The server checks it against the input's types (ADR-0032 D7);
 * this only picks the control, each type's own or its nearest ancestor's (D8), and hints
 * the types in plain words on hover and, in an empty field, on focus (V23).
 */
export default function ValueEditor({ label, accepts, lineages = [], value, disabled = false, onChange }: ValueEditorProps) {
  const { t } = useTranslation();
  const views = editViewsOf(accepts, lineages);
  const hint = typesHint(accepts, t) ?? undefined;
  if (views.has("checkbox")) {
    return <input type="checkbox" aria-label={label} title={hint} checked={value === true} disabled={disabled} onChange={(event) => { onChange(event.target.checked); }} />;
  }
  const numeric = views.has("integer") || views.has("number");
  // A decimal takes any fraction; the browser's default step of 1 would refuse 0.5.
  const step = !numeric ? undefined : views.has("number") ? "any" : 1;
  return (
    <input
      aria-label={label}
      className={`${FORM_COMPACT_CONTROL_CLASS} w-48`}
      type={numeric ? "number" : "text"}
      step={step}
      title={hint}
      placeholder={hint}
      disabled={disabled}
      value={value === null || value === undefined ? "" : typeof value === "string" || typeof value === "number" ? value : JSON.stringify(value)}
      onChange={(event) => { onChange(numeric && event.target.value !== "" ? Number(event.target.value) : event.target.value); }}
    />
  );
}
